/**
 * External dependencies.
 */
import { __ } from '@wordpress/i18n';
import {
	InspectorControls,
	useBlockProps,
	__experimentalUnitControl as UnitControl // eslint-disable-line
} from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	SelectControl,
} from '@wordpress/components';

export default ( props ) => {
	const { attributes: { spacegroupsgap, spacesgap, spacegrouptitlefirstspacegap, spaceGroupTypography, spaceTypography }, setAttributes } = props;

	const blockProps = useBlockProps( {
		style: {
			fontFamily: 'var(--portal-font-family)',
		},
		className: 'suredash-portal__editor-navigation',
	} );

	const spaceActiveText = props?.attributes?.style?.elements?.spaceactivetext?.color?.color || '';
	const spaceActiveBackground = props?.attributes?.style?.elements?.spaceactivebackground?.color?.background || '';
	const spaceGroupText = props?.attributes?.style?.elements?.spacegrouptext?.color?.color || '';
	const spaceGroupBackground = props?.attributes?.style?.elements?.spacegroupbackground?.color?.background || '';

	// Helper function to convert typography object to CSS
	const getTypographyCSS = ( typography ) => {
		if ( ! typography || Object.keys( typography ).length === 0 ) {
			return '';
		}

		let css = '';
		if ( typography.fontSize ) {
			css += `font-size: ${ typography.fontSize };`;
		}
		if ( typography.fontWeight ) {
			css += `font-weight: ${ typography.fontWeight };`;
		}
		if ( typography.lineHeight ) {
			css += `line-height: ${ typography.lineHeight };`;
		}
		if ( typography.letterSpacing ) {
			css += `letter-spacing: ${ typography.letterSpacing };`;
		}
		if ( typography.textTransform ) {
			css += `text-transform: ${ typography.textTransform };`;
		}
		if ( typography.textDecoration ) {
			css += `text-decoration: ${ typography.textDecoration };`;
		}
		return css;
	};

	const spaceGroupTypoCSS = getTypographyCSS( spaceGroupTypography );
	const spaceTypoCSS = getTypographyCSS( spaceTypography );

	const handleSpaceGroupTypographyChange = ( key, value ) => {
		setAttributes( {
			spaceGroupTypography: {
				...spaceGroupTypography,
				[ key ]: value,
			},
		} );
	};

	const handleSpaceTypographyChange = ( key, value ) => {
		setAttributes( {
			spaceTypography: {
				...spaceTypography,
				[ key ]: value,
			},
		} );
	};

	const loopSpaceGroupSet = ( num ) => {
		const spaceGroupSet = [];
		const spacesCount = 3;
		for ( let i = 1; i <= num; i++ ) {
			spaceGroupSet.push(
				<div className="portal-aside-group-wrap" key={ i }>
					<div className="portal-aside-group">
						<div className="portal-aside-group-header">
							<span className="portal-aside-group-title-link sd-no-space">
								<h5 className="portal-aside-group-title"> { __( 'Space Group', 'suredash' ) } { i } </h5>
							</span>
							<span className="sd-aside-group-toggle">
								<span className="portal-svg-icon portal-icon-sm" aria-hidden="true" aria-label="ChevronDown"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-down"><path d="m6 9 6 6 6-6"></path></svg></span>
							</span>
						</div>

						<div className="portal-aside-group-body">
							<ul className="portal-aside-group-list sd-no-space">
								{ /* render li items till spacesCount */ }
								{ Array.from( { length: spacesCount }, ( _, index ) => (
									<li key={ i + index }>
										<a href="javascript:void(0)" className={ i === 1 && index === 0 ? 'portal-aside-group-link active' : 'portal-aside-group-link' }> {/* eslint-disable-line */}
											<span className="portal-svg-icon portal-icon-sm" aria-hidden="true" aria-label="Calendar"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg></span>
											<span className="portal-aside-item-title"> { __( 'Space', 'suredash' ) } { index + 1 } </span>
										</a>
									</li>
								) ) }
							</ul>
						</div>
					</div>
				</div>
			);
		}
		return spaceGroupSet;
	};

	return (
		<>
			<InspectorControls>
				{/* Space Group Typography - appears after color controls */}
				<PanelBody title={ __( 'Space Group Typography', 'suredash' ) } initialOpen={ false }>
					<UnitControl
						label={ __( 'Font Size', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceGroupTypography?.fontSize || '' }
						onChange={ ( fontSize ) => handleSpaceGroupTypographyChange( 'fontSize', fontSize ) }
					/>

					<RangeControl
						label={ __( 'Font Weight', 'suredash' ) }
						value={ parseInt( spaceGroupTypography?.fontWeight ) || 400 }
						onChange={ ( fontWeight ) => handleSpaceGroupTypographyChange( 'fontWeight', String( fontWeight ) ) }
						min={ 100 }
						max={ 900 }
						step={ 100 }
					/>

					<UnitControl
						label={ __( 'Line Height', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceGroupTypography?.lineHeight || '' }
						onChange={ ( lineHeight ) => handleSpaceGroupTypographyChange( 'lineHeight', lineHeight ) }
					/>

					<UnitControl
						label={ __( 'Letter Spacing', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceGroupTypography?.letterSpacing || '' }
						onChange={ ( letterSpacing ) => handleSpaceGroupTypographyChange( 'letterSpacing', letterSpacing ) }
					/>

					<SelectControl
						label={ __( 'Text Transform', 'suredash' ) }
						value={ spaceGroupTypography?.textTransform || '' }
						onChange={ ( textTransform ) => handleSpaceGroupTypographyChange( 'textTransform', textTransform ) }
						options={ [
							{ label: __( 'None', 'suredash' ), value: '' },
							{ label: __( 'Uppercase', 'suredash' ), value: 'uppercase' },
							{ label: __( 'Lowercase', 'suredash' ), value: 'lowercase' },
							{ label: __( 'Capitalize', 'suredash' ), value: 'capitalize' },
						] }
					/>

					<SelectControl
						label={ __( 'Text Decoration', 'suredash' ) }
						value={ spaceGroupTypography?.textDecoration || '' }
						onChange={ ( textDecoration ) => handleSpaceGroupTypographyChange( 'textDecoration', textDecoration ) }
						options={ [
							{ label: __( 'None', 'suredash' ), value: '' },
							{ label: __( 'Underline', 'suredash' ), value: 'underline' },
							{ label: __( 'Overline', 'suredash' ), value: 'overline' },
							{ label: __( 'Line Through', 'suredash' ), value: 'line-through' },
						] }
					/>
				</PanelBody>

				{/* Space Typography */}
				<PanelBody title={ __( 'Space Typography', 'suredash' ) } initialOpen={ false }>
					<UnitControl
						label={ __( 'Font Size', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceTypography?.fontSize || '' }
						onChange={ ( fontSize ) => handleSpaceTypographyChange( 'fontSize', fontSize ) }
					/>

					<RangeControl
						label={ __( 'Font Weight', 'suredash' ) }
						value={ parseInt( spaceTypography?.fontWeight ) || 400 }
						onChange={ ( fontWeight ) => handleSpaceTypographyChange( 'fontWeight', String( fontWeight ) ) }
						min={ 100 }
						max={ 900 }
						step={ 100 }
					/>

					<UnitControl
						label={ __( 'Line Height', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceTypography?.lineHeight || '' }
						onChange={ ( lineHeight ) => handleSpaceTypographyChange( 'lineHeight', lineHeight ) }
					/>

					<UnitControl
						label={ __( 'Letter Spacing', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spaceTypography?.letterSpacing || '' }
						onChange={ ( letterSpacing ) => handleSpaceTypographyChange( 'letterSpacing', letterSpacing ) }
					/>

					<SelectControl
						label={ __( 'Text Transform', 'suredash' ) }
						value={ spaceTypography?.textTransform || '' }
						onChange={ ( textTransform ) => handleSpaceTypographyChange( 'textTransform', textTransform ) }
						options={ [
							{ label: __( 'None', 'suredash' ), value: '' },
							{ label: __( 'Uppercase', 'suredash' ), value: 'uppercase' },
							{ label: __( 'Lowercase', 'suredash' ), value: 'lowercase' },
							{ label: __( 'Capitalize', 'suredash' ), value: 'capitalize' },
						] }
					/>

					<SelectControl
						label={ __( 'Text Decoration', 'suredash' ) }
						value={ spaceTypography?.textDecoration || '' }
						onChange={ ( textDecoration ) => handleSpaceTypographyChange( 'textDecoration', textDecoration ) }
						options={ [
							{ label: __( 'None', 'suredash' ), value: '' },
							{ label: __( 'Underline', 'suredash' ), value: 'underline' },
							{ label: __( 'Overline', 'suredash' ), value: 'overline' },
							{ label: __( 'Line Through', 'suredash' ), value: 'line-through' },
						] }
					/>
				</PanelBody>

				{/* Spacing Controls */}
				<PanelBody title={ __( 'Spacing', 'suredash' ) }>
					<UnitControl
						label={ __( 'Spaces Gap', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spacesgap }
						onChange={ ( spacesgap ) => setAttributes( { spacesgap } ) } // eslint-disable-line
					/>

					<UnitControl
						label={ __( 'Group Title & First Space Gap', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spacegrouptitlefirstspacegap }
						onChange={ ( spacegrouptitlefirstspacegap ) => setAttributes( { spacegrouptitlefirstspacegap } ) } // eslint-disable-line
					/>

					<UnitControl
						label={ __( 'Space Groups Gap', 'suredash' ) }
						labelPosition="top"
						__unstableInputWidth="80px"
						value={ spacegroupsgap }
						onChange={ ( spacegroupsgap ) => setAttributes( { spacegroupsgap } ) } // eslint-disable-line
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ /* Style space groups gap & spaces gap by CSS */ }
				<style>
					{ `
						.wp-block-suredash-navigation .portal-aside-group-body {
							margin-bottom: ${ spacegroupsgap };
							padding-bottom: unset;
						}
						.portal-aside-group-link {
							padding-top:${ spacesgap };
							padding-bottom:${ spacesgap };
							${ spaceTypoCSS }
						}
						.portal-aside-group-header {
							margin-bottom: ${ spacegrouptitlefirstspacegap };
							${ spaceGroupTypoCSS }
						}
						.wp-block-suredash-navigation ul a.active {
							background: ${ spaceActiveBackground };
						}
						.wp-block-suredash-navigation ul a.active, .wp-block-suredash-navigation ul a.active * {
							color: ${ spaceActiveText };
						}
						.portal-aside-group-header {
							background: ${ spaceGroupBackground };
						}
						.portal-aside-group-header .portal-aside-group-title {
							color: ${ spaceGroupText };
						}
					` }
				</style>

				<div className="portal-aside-list-wrapper portal-content">
					{ loopSpaceGroupSet( 4 ) }
				</div>
			</div>
		</>
	);
};
